import React, { useEffect, useMemo, useState, useContext } from 'react';
import * as AppPics from 'assets/images';
import CoursesMultiSelect from 'components/molecules/CoursesMultiSelect';
import TeachersMultiSelect from 'components/molecules/TeachersMultiSelect';
import useDoubts from 'hooks/API/GET/useDoubts';
import useAssignedTeachers from 'hooks/API/GET/useAssignedTeachers';
import useTeacherCourses from 'hooks/API/GET/useTeacherCourses';
import { useHistory, useLocation } from 'react-router-dom';
import useDebounce from 'hooks/useDebounce';
import { UserContext } from 'context/UserContext';
import { AppRadioBox } from 'components';
import TablePaginate from 'components/Paginate';
import usePaginateDefaults from 'hooks/usePaginateDefaults';

function ManageDoubts() {
  const history = useHistory();
  const location = useLocation();
  const { userInfo } = useContext(UserContext) || {};
  const roleKey = (userInfo?.data?.role?.key || userInfo?.data?.key || '').toUpperCase();
  const isAdmin = roleKey === 'ADMIN';
  const isTutor = roleKey === 'TUTOR';

  // --- filters ---
  const urlParams = new URLSearchParams(location.search);
  const initialKeyword = urlParams.get('keyword') || '';
  const initialCourse = urlParams.get('course') || '';
  const initialTeacher = urlParams.get('teacher') || '';
  const initialStatus = urlParams.get('status') || '';
  const initialPage = parseInt(urlParams.get('page')) || 0;

  const [params, setParams] = usePaginateDefaults({ limit: 5, page: initialPage });
  const [enableFilter, setEnableFilter] = useState(false);
  const [searchTerm, setSearchTerm] = useState(initialKeyword);
  const searchKeyword = useDebounce(searchTerm, 800);

  const [selectedCourse, setSelectedCourse] = useState(
    initialCourse ? { value: initialCourse } : null
  );
  const [selectedTeacher, setSelectedTeacher] = useState(initialTeacher || '');
  const [selectedStatus, setSelectedStatus] = useState(initialStatus || '');

  const updateURL = (newParams) => {
    const params = new URLSearchParams(location.search);
    Object.entries(newParams).forEach(([key, value]) => {
      if (value && value !== '') params.set(key, value);
      else params.delete(key);
    });
    const newURL = params.toString()
      ? `${location.pathname}?${params.toString()}`
      : location.pathname;
    history.replace(newURL);
  };

  useEffect(() => {
    updateURL({
      keyword: searchKeyword,
      course: selectedCourse?.value || '',
      teacher: selectedTeacher || '',
      status: selectedStatus || '',
      page: params.page,
      limit: params.limit,
    });
  }, [searchKeyword, selectedCourse, selectedTeacher, selectedStatus]);

  const { data: tutorCoursesData } = useTeacherCourses();

  const { data: allDoubtsResponse } = useDoubts({
    roleKey,
    enabled: true,
    params: {
      page: 0,
      limit: 1000,
    },
  });

  const { data: doubtResponse, isLoading } = useDoubts({
    roleKey,
    enabled: true,
    params: {
      page: params.page,
      limit: params.limit,
      ...(selectedCourse?.value && { course: selectedCourse.value }),
      // Only include teacher filter for admins (not for tutors)
      ...(selectedTeacher && isAdmin && { teacher: selectedTeacher }),
      ...(searchKeyword && { keyword: searchKeyword }),
      ...(selectedStatus && { status: selectedStatus }),
    },
  });

  const allDoubts = allDoubtsResponse?.data?.result || [];
  const doubts = doubtResponse?.data?.result || [];
  const coursesOptions = useMemo(() => {
    if (isTutor) {
      return tutorCoursesData?.map((c) => ({
        value: c._id,
        label: c.courseName,
      }));
    }

    const uniqueCourses = [
      ...new Map(
        allDoubts
          .map((d) => d.course)
          .filter(Boolean)
          .map((c) => [c._id, c])
      ).values(),
    ];

    return uniqueCourses.map((c) => ({
      value: c._id,
      label: c.courseName,
    }));
  }, [allDoubts, tutorCoursesData, isTutor]);

  const selectedCourseId = selectedCourse?.value || '';
  const { data: teachersData } = useAssignedTeachers({
    courseId: selectedCourseId,
    enabled: !!selectedCourseId,
  });

  const teachersOptions = useMemo(() => {
    if (teachersData && teachersData.length > 0) {
      const options = teachersData
        .map((t) => (t?._id && t?.name ? { _id: t._id, name: t.name } : null))
        .filter(Boolean);

      return options;
    }

    if (selectedCourseId && allDoubts.length > 0) {
      const courseDoubts = allDoubts.filter((d) => d.course?._id === selectedCourseId);

      const uniqueTeachers = [
        ...new Map(
          courseDoubts
            .map((d) => {
              if (d.assignedTeacher && typeof d.assignedTeacher === 'object') {
                if (Array.isArray(d.assignedTeacher) && d.assignedTeacher[0]) {
                  return d.assignedTeacher[0];
                }
                if (!Array.isArray(d.assignedTeacher) && d.assignedTeacher._id) {
                  return d.assignedTeacher;
                }
              }
              return null;
            })
            .filter(Boolean)
            .map((t) => [t._id, { _id: t._id, name: t.name }])
        ).values(),
      ];

      return uniqueTeachers;
    }

    return [];
  }, [teachersData, selectedCourseId, allDoubts]);

  useEffect(() => {
    if (selectedTeacher && !teachersOptions.find((t) => t._id === selectedTeacher)) {
      setSelectedTeacher('');
    }
  }, [teachersOptions]);

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') e.preventDefault();
  };

  const handleCourseChange = (selected) => {
    setSelectedCourse(selected);
  };

  const handleTeacherChange = (selectedTeacherId) => {
    setSelectedTeacher(selectedTeacherId || '');
  };

  const handleStatusChange = (value) => {
    setSelectedStatus(value);
  };

  const clearAllFilters = () => {
    setSelectedCourse(null);
    setSelectedTeacher('');
    setSelectedStatus('');
    setSearchTerm('');
    history.replace(location.pathname);
  };

  const handleOpenAnswerPage = (d) => {
    history.push(`/doubtclearance/answer/${d._id}`);
  };

  return (
    <div className="mainWraper coursetabsection">
      <div className="tab">
        <div className="tab__content ___adjust___tab___padding">
          <div className="tab__content-actBody">
            <div className="px-5 py-5">
              {/* HEADER */}
              <div className="tab__contentMenu">
                <ul className="tab__contentMenu-list">
                  <li>
                    <h2 className="prime-head p-0 boldtitle">Manage Doubts</h2>
                  </li>
                </ul>

                <div className="mainHeader__search">
                  <ul className="tab__contentMenu-list">
                    <li className="relative">
                      <input
                        className="mainHeader__searchInput"
                        placeholder="Search doubts..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={handleSearchKeyPress}
                      />
                      <span className="mainHeader__searchBtn">
                        <img src={AppPics.TopMenuSearchSvg} alt="Search" />
                      </span>
                    </li>

                    <li className="px-4 flex items-center">
                      <button
                        type="button"
                        className="flex items-center gap-1"
                        onClick={() => setEnableFilter(!enableFilter)}
                      >
                        <img src={AppPics.TabFilterSvg} alt="Filter" />
                        <span className="viewsettingtitle">Filter</span>
                      </button>
                    </li>
                  </ul>
                </div>
              </div>

              {/* FILTER */}
              {enableFilter && (
                <div className="row mt-3">
                  <div className="col-lg-3">
                    <label className="label d-block">Doubt Status</label>
                    <div className="radio-group">
                      <AppRadioBox
                        inputProps={{
                          name: 'status',
                          value: 'pending',
                          checked: selectedStatus === 'pending',
                          onChange: () => handleStatusChange('pending'),
                        }}
                        labelProps={{ label: 'Pending' }}
                      />
                      <AppRadioBox
                        inputProps={{
                          name: 'status',
                          value: 'resolved',
                          checked: selectedStatus === 'resolved',
                          onChange: () => handleStatusChange('resolved'),
                        }}
                        labelProps={{ label: 'Resolved' }}
                      />
                    </div>
                  </div>

                  <div className="col-lg-3">
                    <CoursesMultiSelect
                      enableAPI={false}
                      values={selectedCourse ? [selectedCourse] : []}
                      handleChange={handleCourseChange}
                      isMulti={false}
                      options={coursesOptions}
                    />
                  </div>

                  {isAdmin && (
                    <div className="col-lg-3">
                      <TeachersMultiSelect
                        value={selectedTeacher}
                        handleChange={(selected) => handleTeacherChange(selected?.value)}
                        options={teachersOptions}
                        isMulti={false}
                      />
                    </div>
                  )}

                  <div className="col-lg-3 d-flex mt-4">
                    <button
                      type="button"
                      className="btn btn-plane p--tint"
                      style={{ alignSelf: 'end' }}
                      onClick={clearAllFilters}
                    >
                      Clear filter
                    </button>
                  </div>
                </div>
              )}

              {/* TABLE */}
              <div className="tab__contentBody mt-4">
                <div className="tableWrap">
                  <div className="table-responsive">
                    <table className="table tableWrap__table">
                      <thead className="thead-light __appTheadColor">
                        <tr>
                          <th className="text-center">#</th>
                          <th className="text-center">Course</th>
                          <th className="text-center">Student</th>
                          <th className="text-center">Question</th>
                          {/* <th className="text-center">Answer</th> */}
                          <th className="text-center">Status</th>
                          <th className="text-center">Created</th>
                          <th className="text-center">Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {isLoading ? (
                          <tr>
                            <td colSpan={8} className="text-center py-4">
                              Loading doubts...
                            </td>
                          </tr>
                        ) : doubts.length > 0 ? (
                          doubts.map((d, i) => (
                            <tr key={d._id}>
                              <td className="text-center">{i + 1}</td>
                              <td className="text-center">{d.course?.courseName || '-'}</td>
                              <td className="text-center">{d.askedBy?.name || '-'}</td>
                              <td className="text-center">{d.question}</td>
                              {/* <td className="text-center">
                                {d.answer || <em className="text-muted">Not answered</em>}
                              </td> */}
                              <td className="text-center">
                                <span
                                  className={`badge ${
                                    d.status === 'resolved' ? 'badge-success' : 'badge-warning'
                                  }`}
                                >
                                  {d.status}
                                </span>
                              </td>
                              <td className="text-center">
                                {new Date(d.createdAt).toLocaleDateString()}
                              </td>
                              <td className="text-center">
                                <button
                                  className="actions-icons mx-2 text-primary"
                                  onClick={() => handleOpenAnswerPage(d)}
                                >
                                  <img src={AppPics.ValidateSvg} alt="Reply" />
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={8} className="text-center py-4">
                              No doubts found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <TablePaginate
                  currentPage={params.page}
                  maxCount={doubtResponse?.meta?.total}
                  defaultLimit={params.limit}
                  setPage={(page) => setParams((p) => ({ ...p, page }))}
                  handleChange={(limit) =>
                    setParams((p) => ({ ...p, page: 0, limit: parseInt(limit) }))
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ManageDoubts;
